/**
 * /portal/scan: the in-portal Prism Labs body scan (repo-ready pack HANDOFF.md step 5; Reference ID sheet v12
 * "Read me" items 2 and 3).
 *
 * The five steps:
 *   1. ScanDueCard on the dashboard while a scan is due (new patients: until the first scan), "Start my body scan".
 *   2. First scan only: consent. Prism's scan terms, the MCOS body scan consent, and an optional research box, off.
 *   3. Get ready: the five tips.
 *   4. Capture (BodyScanCapture.tsx): the video goes from the phone straight to Prism; MCOS never stores it.
 *   5. Processing, then results: body fat, lean mass, waist and skeletal muscle, each next to the first scan, the
 *      sentence that phone scans are estimates, and "Scan again" (a retake, which never moves the first scan).
 *
 * Plain, calm words only. A patient never sees a score, a fit label, a reason in clinical words, or why a measure was
 * not returned; a measure that did not come back says so and nothing is estimated in its place (DEV-BG-03). While
 * Prism is not switched on the page says so and the dashboard card does not show.
 *
 * The Prism terms tick links to the terms themselves. After the first scan, "Your scan choices" shows when the
 * patient agreed and lets them turn research use on or off. The steps bar shows only while a scan is under way; a
 * page opened just to look at results has none. Scan times are the patient's own local time.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PortalApiError, portalRequest } from '../../lib/portal';
import { Card, CardHead, DISPLAY, DISPLAY_MID, Eyebrow, IconTile, PageTitle, PortalEmpty, PortalError, PortalLoading, TONE, formatNumber, usePortalQuery } from './shell';
import { ArrowRight, ChatBubble, Chevron } from './icons';
import { BodyLine, CheckCircle, PhoneLine, ShirtLine, StepBackLine, SunLine, TurnLine } from './BodyScanIcons';
import { BodyScanCapture, type CaptureSession } from './BodyScanCapture';

/* ------------------------------------------------------------------ *
 * What the API sends (GET /portal/scan, GET /portal/scan/scans/:id)
 * ------------------------------------------------------------------ */

type ScanState = 'PENDING' | 'PROCESSING' | 'COMPLETE' | 'PARTIAL' | 'FAILED';

export interface ScanStatus {
  enabled: boolean;
  due?: boolean;
  firstScan?: boolean;
  consent?: { onFile: boolean; researchConsent: boolean; acceptedAt: string | null; termsVersion: string | null };
  terms?: { prismVersion: string | null; prismUrl: string | null; mcosVersion: string; mcosPoints: string[]; draft: boolean };
  tips?: Array<{ key: string; title: string; body: string }>;
  missing?: string[];
  inProgress?: { id: string; state: ScanState } | null;
  scans?: Array<{ id: string; takenAt: string; state: ScanState; isBaseline: boolean }>;
  captureMode?: 'sdk' | 'local-mock';
}

interface ResultRow {
  key: 'bodyFatPct' | 'leanMassLb' | 'waistIn' | 'skeletalMuscleMassLb';
  label: string;
  unit: string;
  value: number | null;
  first: number | null;
  change: number | null;
}

export interface ScanView {
  scan: { id: string; state: ScanState; takenAt: string; checkinMonth: number | null; isBaseline: boolean };
  results: ResultRow[] | null;
  firstScanAt: string | null;
}

interface StartedScan {
  scan: { id: string; state: ScanState };
  capture: CaptureSession;
}

const finished = (s: ScanState) => s === 'COMPLETE' || s === 'PARTIAL' || s === 'FAILED';

/** A scan time is a real moment, so it reads in the patient's own time zone: "Oct 22, 2026". */
function scanDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "10:55 AM", for telling apart two scans on the same day; with seconds when they share the minute too. */
function scanTime(iso: string, seconds = false): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}) });
}

function useScanStatus() {
  return usePortalQuery<ScanStatus>('scan', '/scan');
}

/** "33.9%", "119.8 lb", "36.3 in". */
function measure(value: number, unit: string): string {
  const n = formatNumber(value, Number.isInteger(value) ? 0 : 1);
  return unit === '%' ? `${n}%` : `${n} ${unit}`;
}

/** A plain difference, no colour and no judgement: "1.9 lower than your first scan". */
function changeWords(row: ResultRow): string | null {
  if (row.change === null) return null;
  if (row.change === 0) return 'The same as your first scan';
  const amount = measure(Math.abs(row.change), row.unit === '%' ? 'points' : row.unit).replace('%', '');
  return `${amount} ${row.change < 0 ? 'lower' : 'higher'} than your first scan`;
}

function StateWords({ state }: { state: ScanState }) {
  const words = state === 'COMPLETE' ? 'Measured' : state === 'PARTIAL' ? 'Some measures missing' : state === 'FAILED' ? 'Did not work' : 'Being measured';
  return <>{words}</>;
}

/* ------------------------------------------------------------------ *
 * Small pieces
 * ------------------------------------------------------------------ */

function Headline({ children }: { children: ReactNode }) {
  return (
    <h2 className="font-display text-[20px] font-semibold leading-[1.15]" style={{ ...DISPLAY_MID, color: TONE.heading }}>
      {children}
    </h2>
  );
}

function PrimaryButton({ children, onClick, disabled, testId }: { children: ReactNode; onClick: () => void; disabled?: boolean; testId?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex h-[44px] w-full items-center justify-center gap-2 rounded-[8px] bg-forest text-[14px] font-semibold text-white transition-colors hover:bg-forest-deep disabled:cursor-not-allowed disabled:opacity-50"
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, testId }: { children: ReactNode; onClick: () => void; testId?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-[42px] w-full items-center justify-center gap-2 rounded-[8px] border bg-white text-[13.5px] font-semibold transition-colors hover:bg-mint"
      style={{ borderColor: 'var(--hairline)', color: TONE.forestText }}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

const STEPS = [
  { key: 'consent', label: 'Consent' },
  { key: 'ready', label: 'Get ready' },
  { key: 'capture', label: 'Scan' },
  { key: 'result', label: 'Results' },
] as const;

type Step = 'consent' | 'ready' | 'capture' | 'processing' | 'result';

/** Where the patient is in the scan. Consent is only a step the first time. */
function Stepper({ step, withConsent }: { step: Step; withConsent: boolean }) {
  const steps = STEPS.filter((s) => withConsent || s.key !== 'consent');
  const at = step === 'processing' ? 'result' : step;
  const index = steps.findIndex((s) => s.key === at);
  return (
    <ol className="mb-[10px] flex items-center gap-[6px] px-1" aria-label="Body scan steps">
      {steps.map((s, i) => {
        const done = i < index;
        const current = i === index;
        return (
          <li key={s.key} className="flex min-w-0 flex-1 flex-col gap-[5px]" aria-current={current ? 'step' : undefined}>
            <span className={`h-[4px] rounded-full ${done || current ? 'bg-forest' : ''}`} style={done || current ? undefined : { background: TONE.line }} />
            <span className={`truncate text-[10.5px] leading-none ${current ? 'font-semibold' : 'font-medium'}`} style={{ color: current ? TONE.forestText : TONE.faint }}>
              {s.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

const TIP_ICON: Record<string, ReactNode> = {
  clothes: <ShirtLine size={19} />,
  phone: <PhoneLine size={19} />,
  step_back: <StepBackLine size={19} />,
  light: <SunLine size={19} />,
  turn: <TurnLine size={19} />,
};

const onPhone = () => typeof navigator !== 'undefined' && /iphone|android/i.test(navigator.userAgent);

/* ------------------------------------------------------------------ *
 * Step 2: consent (first scan only)
 * ------------------------------------------------------------------ */

function ConsentStep({ status, onDone }: { status: ScanStatus; onDone: () => void }) {
  const client = useQueryClient();
  const [terms, setTerms] = useState(false);
  const [mcos, setMcos] = useState(false);
  const [research, setResearch] = useState(false);
  const save = useMutation({
    mutationFn: () => portalRequest<ScanStatus>('/scan/consent', { method: 'POST', body: { prismTerms: true, mcosConsent: true, research } }),
    onSuccess: (next) => {
      client.setQueryData(['portal', 'scan', '/scan'], next);
      onDone();
    },
  });
  const version = status.terms?.prismVersion;
  const termsUrl = status.terms?.prismUrl;
  // Test mode only: the MCOS consent text is still a draft waiting for approved wording (never shown in production).
  const draft = status.terms?.draft && status.captureMode === 'local-mock';
  return (
    <Card className="px-[14px] pb-[14px] pt-[13px]" data-testid="scan-consent">
      <CardHead label={askedBefore(status) ? 'Before your next scan' : 'Before your first scan'} />
      <div className="mt-[10px] flex flex-wrap items-center gap-x-[8px] gap-y-[4px]">
        <Headline>How your body scan works</Headline>
        {draft && (
          <span className="rounded-[4px] border border-dashed px-[6px] py-[2px] text-[10.5px] font-medium leading-none" style={{ borderColor: 'var(--hairline)', color: TONE.faint }} data-testid="consent-draft">
            Draft wording, test mode
          </span>
        )}
      </div>
      {askedBefore(status) && (
        <p className="mt-[6px] text-[12.5px] leading-snug" style={{ color: TONE.body }}>
          The wording has been updated since you last agreed, so we are asking again.
        </p>
      )}
      <ul className="mt-[10px] space-y-[8px]">
        {(status.terms?.mcosPoints ?? []).map((point) => (
          <li key={point} className="flex gap-[9px] text-[13px] leading-[1.45]" style={{ color: TONE.body }}>
            <span className="mt-[1px] shrink-0 text-forest">
              <CheckCircle size={17} strokeWidth={1.7} />
            </span>
            {point}
          </li>
        ))}
      </ul>

      <div className="mt-[14px] space-y-[8px] rounded-[8px] px-[10px] py-[10px]" style={{ background: TONE.row }}>
        <Tick checked={terms} onChange={setTerms} testId="consent-prism">
          I accept the{' '}
          {termsUrl ? (
            <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-2" style={{ color: TONE.forestText }} data-testid="consent-prism-link">
              Prism Labs scan terms
            </a>
          ) : (
            'Prism Labs scan terms'
          )}
          {version ? ` (version ${version})` : ''}.
        </Tick>
        <Tick checked={mcos} onChange={setMcos} testId="consent-mcos">
          I agree to the MCOS body scan consent above.
        </Tick>
      </div>
      <div className="mt-[8px] px-[10px]">
        <Tick checked={research} onChange={setResearch} testId="consent-research">
          Prism Labs may also use my scan for research. <span style={{ color: TONE.faint }}>Optional.</span>
        </Tick>
      </div>

      {save.error && (
        <p className="mt-[10px] text-[12.5px] text-alert" role="alert">
          {save.error instanceof Error ? save.error.message : 'Could not save. Try again.'}
        </p>
      )}
      <div className="mt-[14px]">
        <PrimaryButton onClick={() => save.mutate()} disabled={!terms || !mcos || save.isPending} testId="consent-continue">
          {save.isPending ? 'Saving' : 'Continue'}
        </PrimaryButton>
      </div>
      <p className="mt-[8px] text-center text-[11.5px] leading-snug" style={{ color: TONE.faint }}>
        Your care team can see that you agreed, and when.
      </p>
    </Card>
  );
}

function Tick({ checked, onChange, children, testId }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode; testId: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-[10px] text-[13px] leading-[1.4]" style={{ color: TONE.heading }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-[1px] h-[18px] w-[18px] shrink-0 cursor-pointer rounded accent-[var(--forest)]"
        data-testid={testId}
      />
      <span>{children}</span>
    </label>
  );
}

/* ------------------------------------------------------------------ *
 * Step 3: get ready
 * ------------------------------------------------------------------ */

/** The real scan runs on the phone's camera: from a computer, the page sends the patient to their phone. */
function UsePhoneCard() {
  const address = typeof window !== 'undefined' ? `${window.location.host}/portal/scan` : 'your portal';
  return (
    <Card className="px-[14px] py-[14px]" data-testid="scan-use-phone">
      <div className="flex gap-[12px]">
        <IconTile size={36} round>
          <PhoneLine size={19} />
        </IconTile>
        <div className="min-w-0">
          <Headline>Open your portal on your phone</Headline>
          <p className="mt-[5px] text-[13px] leading-relaxed" style={{ color: TONE.body }}>
            The body scan uses your phone's camera. On your phone, sign in to your portal and go to{' '}
            <span className="font-semibold" style={{ color: TONE.heading }}>
              {address}
            </span>
            .
          </p>
        </div>
      </div>
    </Card>
  );
}

function ReadyStep({ status, onStarted, onNeedConsent }: { status: ScanStatus; onStarted: (s: StartedScan) => void; onNeedConsent: () => void }) {
  const client = useQueryClient();
  const testMode = status.captureMode === 'local-mock';
  const start = useMutation({
    mutationFn: () => portalRequest<StartedScan>('/scan/start', { method: 'POST', body: {} }),
    onSuccess: (started) => onStarted(started),
    onError: (error) => {
      if (error instanceof PortalApiError && error.code === 'SCAN_CONSENT_REQUIRED') onNeedConsent();
      void client.invalidateQueries({ queryKey: ['portal', 'scan'] });
    },
  });
  return (
    <Card className="px-[14px] pb-[14px] pt-[13px]" data-testid="scan-ready">
      <CardHead label="Get ready" />
      <div className="mt-[10px]">
        <Headline>Five things for a good scan</Headline>
      </div>
      <ol className="mt-[10px] space-y-[6px]">
        {(status.tips ?? []).map((tip) => (
          <li key={tip.key} className="flex items-center gap-[12px] rounded-[8px] py-[8px] pl-[6px] pr-[10px]" style={{ background: TONE.row }}>
            <IconTile size={36}>{TIP_ICON[tip.key] ?? <BodyLine size={19} />}</IconTile>
            <span className="min-w-0">
              <span className="block text-[13.5px] font-semibold leading-tight" style={{ color: TONE.heading }}>
                {tip.title}
              </span>
              <span className="mt-[2px] block text-[12px] leading-snug" style={{ color: TONE.body }}>
                {tip.body}
              </span>
            </span>
          </li>
        ))}
      </ol>
      {!onPhone() && testMode && (
        <p className="mt-[10px] text-[12px] leading-snug" style={{ color: TONE.faint }} data-testid="scan-desktop-test-note">
          The scan uses your phone's camera. This portal is in test mode, so you can also try it on this computer.
        </p>
      )}
      {start.error && !(start.error instanceof PortalApiError && start.error.code === 'SCAN_CONSENT_REQUIRED') && (
        <p className="mt-[10px] text-[12.5px] text-alert" role="alert">
          {start.error instanceof Error ? start.error.message : 'The scan could not start. Try again.'}
        </p>
      )}
      <div className="mt-[14px]">
        <PrimaryButton onClick={() => start.mutate()} disabled={start.isPending} testId="scan-start">
          {start.isPending ? 'Starting' : "I'm ready, start my scan"}
          {!start.isPending && <ArrowRight size={15} strokeWidth={2} />}
        </PrimaryButton>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Step 5: processing, then results
 * ------------------------------------------------------------------ */

function useScanView(scanId: string | null) {
  return useQuery({
    queryKey: ['portal', 'scan-view', scanId],
    queryFn: () => portalRequest<ScanView>(`/scan/scans/${scanId}`),
    enabled: Boolean(scanId),
    retry: false,
    refetchInterval: (query) => {
      const state = query.state.data?.scan.state;
      return state && !finished(state) ? 3000 : false;
    },
  });
}

function ProcessingCard() {
  return (
    <Card className="px-[14px] py-[22px] text-center" data-testid="scan-processing">
      <span className="mx-auto block h-[42px] w-[42px] animate-spin rounded-full border-[3px]" style={{ borderColor: TONE.line, borderTopColor: 'var(--forest)' }} aria-hidden="true" />
      <div className="mt-[14px]" aria-live="polite">
        <Headline>Measuring your scan</Headline>
      </div>
      <p className="mx-auto mt-[6px] max-w-[300px] text-[13px] leading-relaxed" style={{ color: TONE.body }}>
        Prism Labs is working out your measurements. You can leave this page; your results will be here when you come
        back.
      </p>
    </Card>
  );
}

function ResultCard({ view }: { view: ScanView }) {
  const partial = view.scan.state === 'PARTIAL';
  const rows = view.results ?? [];
  return (
    <Card className="px-[14px] pb-[12px] pt-[12px]" data-testid="scan-results">
      <CardHead label="Your results" />
      {partial && (
        <p className="mt-[9px] rounded-[8px] bg-risk-yellow-soft px-[10px] py-[8px] text-[12.5px] leading-snug" style={{ color: TONE.heading }} data-testid="scan-partial">
          Some measurements did not come through this time. You can scan again whenever you like.
        </p>
      )}
      <ul className="mt-[8px] divide-y" style={{ borderColor: TONE.line }}>
        {rows.map((row) => {
          const change = changeWords(row);
          return (
            <li key={row.key} className="flex items-start justify-between gap-3 py-[11px]" style={{ borderColor: TONE.line }} data-testid={`result-${row.key}`}>
              <div className="min-w-0">
                <Eyebrow>{row.label}</Eyebrow>
                {row.value === null ? (
                  <p className="mt-[8px] text-[13px] leading-none" style={{ color: TONE.faint }}>
                    Not measured this time
                  </p>
                ) : (
                  <p className="mt-[6px] font-display text-[24px] font-semibold leading-none" style={{ ...DISPLAY, color: TONE.heading }}>
                    {measure(row.value, row.unit)}
                  </p>
                )}
                {change && (
                  <p className="mt-[6px] text-[11.5px] leading-none" style={{ color: TONE.faint }}>
                    {change}
                  </p>
                )}
              </div>
              {!view.scan.isBaseline && (
                <div className="shrink-0 text-right">
                  <p className="text-[10.5px] font-medium uppercase leading-none tracking-[0.05em]" style={{ color: TONE.faint }}>
                    First scan
                  </p>
                  <p className="mt-[7px] text-[15px] font-semibold leading-none" style={{ color: TONE.body }}>
                    {row.first === null ? '-' : measure(row.first, row.unit)}
                  </p>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {view.scan.isBaseline ? (
        <p className="mt-[4px] text-[12.5px] leading-snug" style={{ color: TONE.body }}>
          This is your first scan. Your next scans will show beside it.
        </p>
      ) : view.firstScanAt ? (
        <p className="mt-[4px] text-[12px] leading-snug" style={{ color: TONE.faint }}>
          Your first scan was on {scanDay(view.firstScanAt)}.
        </p>
      ) : null}
      <p className="mt-[10px] border-t pt-[10px] text-[12.5px] leading-snug" style={{ borderColor: TONE.line, color: TONE.body }} data-testid="scan-estimate">
        Phone scans are estimates. They are made for following change from month to month.
      </p>
    </Card>
  );
}

function FailedCard() {
  return (
    <Card className="px-[14px] py-[14px]" data-testid="scan-failed">
      <div className="flex gap-[12px]">
        <IconTile size={34} round tone="cream">
          <BodyLine size={18} />
        </IconTile>
        <div className="min-w-0">
          <Headline>This scan did not work</Headline>
          <p className="mt-[5px] text-[13px] leading-relaxed" style={{ color: TONE.body }}>
            Nothing could be measured from it. Please try again, with your whole body on the screen.
          </p>
        </div>
      </div>
    </Card>
  );
}

function ScanHistory({ scans, current }: { scans: NonNullable<ScanStatus['scans']>; current: string | null }) {
  if (scans.length < 2) return null;
  // Two scans on the same day also show the time, so they can be told apart.
  const repeated = (labels: string[]) => new Set(labels.filter((label, i) => labels.indexOf(label) !== i));
  const days = scans.map((s) => scanDay(s.takenAt));
  const sharedDay = repeated(days);
  const sharedMinute = repeated(scans.map((s, i) => `${days[i]} ${scanTime(s.takenAt)}`));
  const when = (s: (typeof scans)[number], i: number) => {
    if (!sharedDay.has(days[i]!)) return null;
    return scanTime(s.takenAt, sharedMinute.has(`${days[i]} ${scanTime(s.takenAt)}`));
  };
  return (
    <Card className="px-[14px] py-[11px]">
      <CardHead label="Your scans" />
      <ul className="mt-[5px] divide-y" style={{ borderColor: TONE.line }}>
        {scans.map((s, i) => (
          <li key={s.id} style={{ borderColor: TONE.line }}>
            <Link
              to={`/portal/scan?scan=${s.id}`}
              className="flex items-center justify-between gap-3 py-[9px]"
              aria-current={s.id === current ? 'page' : undefined}
            >
              <span className="min-w-0">
                <span className={`block text-[13px] ${s.id === current ? 'font-semibold' : ''}`} style={{ color: TONE.heading }}>
                  {scanDay(s.takenAt)}
                  {when(s, i) && <span style={{ color: TONE.body }}>{`, ${when(s, i)}`}</span>}
                  {s.isBaseline && (
                    <span className="ml-[8px] rounded-full bg-mint px-[7px] py-[2px] text-[10.5px] font-semibold" style={{ color: TONE.forestText }}>
                      First scan
                    </span>
                  )}
                </span>
                <span className="mt-[2px] block text-[11.5px]" style={{ color: TONE.faint }}>
                  <StateWords state={s.state} />
                </span>
              </span>
              <span className="shrink-0" style={{ color: TONE.faint }}>
                <Chevron size={14} strokeWidth={1.9} />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Asked to consent again after agreeing to an earlier version of the texts. */
function askedBefore(status: ScanStatus): boolean {
  return Boolean(status.consent && !status.consent.onFile && status.consent.acceptedAt);
}

/**
 * "Your scan choices": when the patient agreed, a link back to the Prism terms, and research use on or off. Turning
 * research off is always possible; Prism Labs is told straight away when it already knows the patient, otherwise at
 * the next scan.
 */
function ScanChoices({ status }: { status: ScanStatus }) {
  const client = useQueryClient();
  const [note, setNote] = useState<string | null>(null);
  const research = status.consent?.researchConsent ?? false;
  const save = useMutation({
    mutationFn: (next: boolean) =>
      portalRequest<{ researchConsent: boolean; prismUpdated: boolean; status: ScanStatus }>('/scan/research', { method: 'POST', body: { research: next } }),
    onSuccess: (result) => {
      client.setQueryData(['portal', 'scan', '/scan'], result.status);
      setNote(result.prismUpdated ? 'Saved.' : 'Saved. Prism Labs will get your choice at your next scan.');
    },
    onError: () => setNote(null),
  });
  if (!status.consent?.onFile || !status.consent.acceptedAt) return null;
  const termsUrl = status.terms?.prismUrl;
  return (
    <Card className="px-[14px] py-[11px]" data-testid="scan-choices">
      <CardHead label="Your scan choices" />
      <p className="mt-[8px] text-[12.5px] leading-snug" style={{ color: TONE.body }}>
        You agreed to the body scan consent on {scanDay(status.consent.acceptedAt)}
        {termsUrl ? (
          <>
            {' '}and the{' '}
            <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-2" style={{ color: TONE.forestText }}>
              Prism Labs scan terms
            </a>
            {status.consent.termsVersion ? ` (version ${status.consent.termsVersion})` : ''}
          </>
        ) : null}
        .
      </p>
      <label className="mt-[8px] flex items-center justify-between gap-3 border-t pt-[9px] text-[13px]" style={{ borderColor: TONE.line, color: TONE.heading }}>
        <span>Prism Labs may use my scans for research</span>
        <input
          type="checkbox"
          role="switch"
          checked={research}
          disabled={save.isPending}
          onChange={(e) => {
            setNote(null);
            save.mutate(e.target.checked);
          }}
          className="h-4 w-4 shrink-0 cursor-pointer accent-[var(--forest)]"
          data-testid="scan-research-switch"
        />
      </label>
      <p className="mt-[4px] text-[11.5px] leading-snug" style={{ color: save.error ? undefined : TONE.faint }} role={save.error ? 'alert' : undefined}>
        {save.error ? (
          <span className="text-alert">{save.error instanceof Error ? save.error.message : 'Could not save. Try again.'}</span>
        ) : (
          note ?? 'Optional. You can change this at any time.'
        )}
      </p>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * The page
 * ------------------------------------------------------------------ */

export function PortalBodyScan() {
  const status = useScanStatus();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const linked = params.get('scan');
  const [step, setStep] = useState<Step | null>(null);
  const [scanId, setScanId] = useState<string | null>(null);
  const [session, setSession] = useState<StartedScan | null>(null);
  // Whether this visit began without consent on file: then consent is shown as a step.
  const [askedConsent, setAskedConsent] = useState<boolean | null>(null);
  // Whether a scan is under way in this visit (the steps bar shows only then, not on a page opened to see results).
  const [inFlow, setInFlow] = useState(false);

  const data = status.data;

  // Where the page opens: a linked scan, a scan being measured, the next step of a due scan, or the latest result.
  useEffect(() => {
    if (!data?.enabled || step !== null) return;
    if (askedConsent === null) setAskedConsent(!data.consent?.onFile);
    if (linked) {
      setScanId(linked);
      setStep('result');
      return;
    }
    if (data.inProgress) {
      setScanId(data.inProgress.id);
      setStep('processing');
      setInFlow(true);
      return;
    }
    const latest = data.scans?.[0];
    if (!data.due && latest) {
      setScanId(latest.id);
      setStep('result');
      return;
    }
    setStep(data.consent?.onFile ? 'ready' : 'consent');
    setInFlow(true);
  }, [data, step, linked, askedConsent]);

  // A history link chosen on the results screen.
  useEffect(() => {
    if (linked && step === 'result' && linked !== scanId) setScanId(linked);
  }, [linked, step, scanId]);

  const view = useScanView(step === 'processing' || step === 'result' ? scanId : null);

  useEffect(() => {
    const state = view.data?.scan.state;
    if (step === 'processing' && state && finished(state)) {
      setStep('result');
      void client.invalidateQueries({ queryKey: ['portal', 'scan'] });
    }
  }, [view.data, step, client]);

  const subtitle = useMemo(() => {
    if (!data?.enabled) return undefined;
    if (step === 'result' && view.data) return `Scanned ${scanDay(view.data.scan.takenAt)}`;
    return data.firstScan ? 'Your first body scan, with your phone.' : 'Your monthly body scan, with your phone.';
  }, [data, step, view.data]);

  if (status.isLoading) return <PortalLoading />;
  if (status.error || !data) {
    return <PortalError message={status.error instanceof Error ? status.error.message : 'Could not load your body scan'} retry={() => void status.refetch()} />;
  }

  if (!data.enabled) {
    return (
      <div className="pb-1" data-testid="scan-off">
        <PageTitle title="Body scan" />
        <PortalEmpty
          title="Body scans are not switched on yet"
          body="When your clinic switches them on, you can do your body scan here with your phone. Nothing is needed from you until then."
          action={
            <Link to="/portal" className="text-[13px] font-semibold hover:underline" style={{ color: TONE.forestText }}>
              Back to your dashboard
            </Link>
          }
        />
      </div>
    );
  }

  const missing = data.missing ?? [];
  const scanAgain = () => {
    setParams({}, { replace: true });
    setSession(null);
    setScanId(null);
    setInFlow(true);
    setStep(data.consent?.onFile ? 'ready' : 'consent');
  };
  // The real capture needs the phone's camera; test mode works on a computer too.
  const usePhone = data.captureMode === 'sdk' && !onPhone();

  const needsDetails = missing.length > 0 && (step === 'ready' || step === 'consent');

  return (
    <div className="space-y-[8px] pb-1" data-testid="scan-page">
      <PageTitle title="Body scan" subtitle={subtitle} />
      {/* Only while a scan is under way: not for results opened to look at, or a past scan from the list. */}
      {step && inFlow && !(step === 'result' && linked) && <Stepper step={step} withConsent={askedConsent === true} />}

      {needsDetails ? (
        <Card className="px-[14px] py-[13px]" data-testid="scan-missing">
          <CardHead label="Before your first scan" />
          <p className="mt-[9px] text-[13px] leading-relaxed" style={{ color: TONE.body }}>
            Your care team needs to add your {joinWords(missing)} to your record before your scan can start.
          </p>
          <Link to="/portal/messages/new" className="mt-[12px] flex h-[40px] items-center justify-center gap-2 rounded-[8px] bg-forest text-[13px] font-semibold text-white transition-colors hover:bg-forest-deep">
            <ChatBubble size={15} strokeWidth={1.8} />
            Message your care team
          </Link>
        </Card>
      ) : step === 'consent' ? (
        <ConsentStep status={data} onDone={() => setStep('ready')} />
      ) : step === 'ready' && usePhone ? (
        <UsePhoneCard />
      ) : step === 'ready' ? (
        <ReadyStep
          status={data}
          onNeedConsent={() => {
            setAskedConsent(true);
            setStep('consent');
          }}
          onStarted={(started) => {
            setSession(started);
            setScanId(started.scan.id);
            setStep('capture');
          }}
        />
      ) : step === 'capture' && session && scanId ? (
        <BodyScanCapture
          scanId={scanId}
          session={session.capture}
          onBack={() => setStep('ready')}
          onCaptured={(next) => {
            client.setQueryData(['portal', 'scan-view', next.scan.id], next);
            setStep(finished(next.scan.state) ? 'result' : 'processing');
            void client.invalidateQueries({ queryKey: ['portal', 'scan'] });
          }}
        />
      ) : step === 'processing' ? (
        view.error ? (
          <PortalError message={view.error instanceof Error ? view.error.message : 'Could not load your scan'} retry={() => void view.refetch()} />
        ) : (
          <ProcessingCard />
        )
      ) : step === 'result' ? (
        view.isLoading ? (
          <PortalLoading />
        ) : view.error || !view.data ? (
          <PortalError message={view.error instanceof Error ? view.error.message : 'Could not load your scan'} retry={() => void view.refetch()} />
        ) : !finished(view.data.scan.state) ? (
          <ProcessingCard />
        ) : view.data.scan.state === 'FAILED' ? (
          <FailedCard />
        ) : (
          <ResultCard view={view.data} />
        )
      ) : (
        <PortalLoading />
      )}

      {step === 'result' && view.data && finished(view.data.scan.state) && (
        <div className="space-y-[8px]">
          <SecondaryButton onClick={scanAgain} testId="scan-again">
            <TurnLine size={16} strokeWidth={2} />
            Scan again
          </SecondaryButton>
          <PrimaryButton onClick={() => navigate('/portal')}>Back to dashboard</PrimaryButton>
        </div>
      )}
      {step === 'result' && <ScanHistory scans={data.scans ?? []} current={scanId} />}
      {step === 'result' && <ScanChoices status={data} />}
    </div>
  );
}

function joinWords(words: string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/* ------------------------------------------------------------------ *
 * Step 1: the dashboard card
 * ------------------------------------------------------------------ */

/** "Your monthly body scan is due", on the portal home while a scan is due. Nothing while Prism is off or loading. */
export function ScanDueCard() {
  const { data } = useScanStatus();
  if (!data?.enabled || !data.due) return null;
  const measuring = Boolean(data.inProgress);
  return (
    <Card className="px-[14px] pb-[13px] pt-[12px]" style={{ borderColor: 'var(--mint)' }} data-testid="scan-due-card">
      <div className="flex gap-[12px]">
        <IconTile size={38} round>
          <BodyLine size={20} />
        </IconTile>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase leading-none tracking-[0.08em]" style={{ color: TONE.forestText }}>
            Body scan
          </p>
          <h2 className="mt-[6px] font-display text-[18px] font-semibold leading-[1.15]" style={{ ...DISPLAY_MID, color: TONE.heading }}>
            {measuring ? 'Your body scan is being measured' : data.firstScan ? 'Your first body scan' : 'Your monthly body scan is due'}
          </h2>
          <p className="mt-[4px] text-[12.5px] leading-snug" style={{ color: TONE.body }}>
            {measuring ? 'Your results will appear here when they are ready.' : 'Scan with your phone, right here in your portal.'}
          </p>
        </div>
      </div>
      <Link
        to="/portal/scan"
        className="mt-[11px] flex h-[40px] items-center justify-center gap-2 rounded-[8px] bg-forest text-[13.5px] font-semibold text-white transition-colors hover:bg-forest-deep"
        data-testid="scan-due-start"
      >
        {measuring ? 'See my scan' : 'Start my body scan'}
        <ArrowRight size={15} strokeWidth={2} />
      </Link>
    </Card>
  );
}
